import React from "react";
import Typography from "@mui/material/Typography";

import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActionArea from "@mui/material/CardActionArea";
import Box from "@mui/material/Box";

import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";

import CircularProgress from "@mui/material/CircularProgress";

import { Skeleton } from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

import { useTheme } from "@mui/material/styles";
import { Tooltip } from "@mui/material";
import StrategyDisplayProps from "../StrategyDisplayProps";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";

const minCardWidth = 200;
const maxCardWidth = 300;
const cardHeight = 200;
const enterToolTipDelay = 400;

type StrategyCardProps = {
  strategyRetrievalErrored: boolean;
  title: string;
  supratitle: string;
  description: string;
  selected: boolean;
  primary: boolean;
  loading: boolean;
  onPrimaryClick: () => void;
  onCardClick: () => void;
  onEditClick: () => void;
  strategyDisplayProps: StrategyDisplayProps;
  ranking?: number;
};

function StrategyCard(props: StrategyCardProps) {
  const theme = useTheme();
  const border = props.selected ? 1 : 0;
  const borderWidth = 2;
  const borderColor = props.selected ? theme.palette.primary.dark : theme.palette.background.paper;
  const backgroundColor = props.selected ? theme.palette.primary.light : theme.palette.background.paper;
  const titleTextColor = props.selected ? theme.palette.primary.contrastText : theme.palette.primary.main;
  const mainTextColor = props.selected ? theme.palette.primary.contrastText : theme.palette.text.primary;
  const supraTitleTextColor = theme.palette.text.secondary;
  const rankingColor = props.ranking
    ? props.ranking === 1
      ? "gold"
      : props.ranking === 2
      ? "silver"
      : props.ranking === 3
      ? "brown"
      : undefined
    : undefined;

  return (
    <Card
      sx={{
        maxWidth: maxCardWidth,
        minWidth: minCardWidth,
        minHeight: cardHeight,
        maxHeight: cardHeight,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        backgroundColor: backgroundColor,
        border: border,
        borderWidth: borderWidth,
        borderColor: borderColor,
        zIndex: 0,
        position: "relative",
      }}
    >
      <CardActionArea
        onClick={props.onCardClick}
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          overflow: "hidden",
          justifyContent: "flex-start",
          alignItems: "flex-start",
        }}
      >
        {/* Box is for the padding spacing */}
        <Box sx={{ flexShrink: 0, width: "100%", height: theme.spacing(2), backgroundColor: backgroundColor }} />
        <CardContent sx={{ width: "100%", flexGrow: 1, overflowY: "auto", padding: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", paddingRight: 1 }}>
            <Tooltip title={props.supratitle} placement="top-start" enterDelay={enterToolTipDelay}>
              <Typography
                variant="subtitle1"
                color={supraTitleTextColor}
                sx={{
                  px: 2,
                  lineHeight: "1.25rem",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                }}
              >
                {props.supratitle}
              </Typography>
            </Tooltip>
            {rankingColor ? (
              <Tooltip
                title={
                  props.ranking === 1
                    ? "Best Historical Performance"
                    : props.ranking === 2
                    ? "2nd Best Historical Performance"
                    : props.ranking === 3
                    ? "3rd Best Historical Performance"
                    : null
                }
                placement="top"
              >
                <EmojiEventsIcon fontSize="inherit" style={{ color: rankingColor }} sx={{ alignSelf: "center" }} />
              </Tooltip>
            ) : null}
          </Box>
          <Tooltip title={props.title} placement="top-start" enterDelay={enterToolTipDelay}>
            <Typography
              variant="h6"
              color={titleTextColor}
              sx={{
                position: "sticky",
                top: 0,
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden", //   overflowX: "auto", enable this and delete ellipsis if we want side scroll instead
                background: backgroundColor,
                px: 2,
              }}
            >
              {props.title}
            </Typography>
          </Tooltip>
          <Typography component="div" variant="body2" color={mainTextColor} sx={{ px: 2, pb: 2 }}>
            {[
              {
                description: "Total",
                value: props.strategyDisplayProps.totalValue,
                decimals: 0,
                prepend: "$",
                display: true,
                bold: true,
              },
              {
                description: props.strategyDisplayProps.symbol || "Plan Stock",
                value: props.strategyDisplayProps.coreStockValue,
                decimals: 0,
                prepend: "$",
                display: true,
              },
              {
                description: props.strategyDisplayProps.otherSymbol || "Other Stock",
                value: props.strategyDisplayProps.otherStockValue,
                decimals: 0,
                prepend: "$",
                display: true,
              },
              {
                description: "Cash",
                value:
                  props.strategyDisplayProps.cashValue < 0 && props.strategyDisplayProps.cashValue > -0.1
                    ? 0
                    : props.strategyDisplayProps.cashValue, // if negative ten cents make it zero for some rounding error
                decimals: 0,
                prepend: "$",
                display: true,
              },
            ].map((label, index) =>
              label.display ? (
                <Box
                  key={index}
                  sx={{ display: "flex", flexDirection: "row", fontWeight: label.bold ? 500 : undefined }}
                >
                  <Box sx={{ flexGrow: 1 }}>
                    {label.description ? label.description + " " : <Skeleton width={50} />}
                  </Box>
                  <Box>
                    {props.strategyRetrievalErrored ? (
                      label.prepend + "-"
                    ) : label.value < 0 || !props.strategyDisplayProps.updated ? (
                      <Skeleton width={50} />
                    ) : (
                      label.prepend + Number(Number(label.value).toFixed(label.decimals)).toLocaleString("en-US")
                    )}
                  </Box>
                </Box>
              ) : null
            )}
          </Typography>
        </CardContent>
      </CardActionArea>
      {props.selected || props.primary ? (
        <Tooltip
          title={
            props.primary
              ? "This is your currently selected strategy. You will get email notifications for trades in the future. This strategy also impacts other displays like those in Overview."
              : "Click to lock in and select this strategy. You will get email notifications for trades in the future. This strategy will be used in other displays like those in Overview."
          }
          placement="top-start"
          arrow
          enterDelay={enterToolTipDelay}
        >
          <Button
            variant={"contained"}
            disabled={props.loading}
            size="small"
            color={props.primary ? "success" : "warning"}
            startIcon={props.primary ? <DoneAllOutlinedIcon /> : <LockOpenOutlinedIcon />}
            onClick={props.onPrimaryClick}
            sx={{ position: "absolute", bottom: 0, top: "auto", alignSelf: "center", zIndex: 2, mb: 1 }}
          >
            {props.primary ? "Locked In" : "Lock In"}
          </Button>
        </Tooltip>
      ) : null}
      <IconButton
        size="small"
        onClick={props.onEditClick}
        sx={{ position: "absolute", zIndex: 1, left: "auto", top: "auto", right: 0, bottom: 0 }}
      >
        {props.loading ? <CircularProgress size="2rem" /> : <EditIcon fontSize="small" />}
      </IconButton>
    </Card>
  );
}

export default StrategyCard;
